/**
 * Form14_ReviewOfSystems.jsx
 * Collection: review_of_systems
 * Redirect: /forms/15-telemedicine-consent
 */
import React, { useRef, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { FormShell, Field, SigBlock, saveToMongo, downloadPDF, useToast, C } from "../shared/formKit";

const API_URL = "/api/forms/review-of-systems";
const NEXT    = "/forms/15-telemedicine-consent";

const ROS = {
  allergicImmunologic: { label:"Allergic / Immunologic", items:[
    "Frequent Sneezing","Hives","Itching","Runny Nose","Sinus Pressure",
  ]},
  cardiovascular: { label:"Cardiovascular", items:[
    "Arm Pain on Exertion","Chest Pain on Exertion","Chest Heaviness/Pressure on Exertion",
    "Irregular Heart Beats (Palpitations)","Known Heart Murmur","Light-headed on Standing",
    "Shortness of Breath When Lying Down","Shortness of Breath When Walking","Swelling (Edema)",
  ]},
  constitutional: { label:"Constitutional", items:[
    "Exercise Intolerance","Fatigue","Fever","Weight Gain","Weight Loss",
  ]},
  eyes: { label:"Eyes", items:["Dry Eyes","Irritation","Vision Change"]},
  earNoseMouthThroat: { label:"Ears / Nose / Mouth / Throat", items:[
    "Bleeding Gums","Difficulty Hearing","Dizziness","Dry Mouth","Ear Pain",
    "Frequent Infections","Frequent Nosebleeds","Hoarseness","Mouth Breathing","Mouth Ulcers",
    "Nose/Sinus Problems","Ringing in Ears",
  ]},
  endocrine: { label:"Endocrine", items:["Fatigue","Increased Thirst/Hunger/Urination"]},
  gastrointestinal: { label:"Gastrointestinal", items:[
    "Abdominal Pain","Black or Tarry Stool","Blood in Stool","Change in Appetite",
    "Frequent Indigestion","Hemorrhoids","Trouble Swallowing","Vomiting","Vomiting Blood",
  ]},
  genitourinary: { label:"Genitourinary", items:[
    "Blood in Urine","Difficulty Urinating","Incomplete Emptying","Increased Urinary Frequency","Urinary Loss of Control",
  ]},
  hematologicLymphatic: { label:"Hematologic / Lymphatic", items:["Easy Bruising/Bleeding","Swollen Glands"]},
  integumentary: { label:"Integumentary (Skin)", items:[
    "Changes in Moles","Dry Skin","Eczema","Growth/Lesions","Itching","Jaundice (Yellow Skin/Eyes)","Rash",
  ]},
  musculoskeletal: { label:"Musculoskeletal", items:["Back Pain","Joint Pain","Muscle Aches","Muscle Weakness"]},
  neurological: { label:"Neurological", items:[
    "Dizziness","Fainting","Headaches","Memory Loss","Migraines","Numbness","Restless Legs","Seizures","Weakness",
  ]},
  psychiatric: { label:"Psychiatric", items:[
    "Alcohol Overuse","Anxiety/Stress","Depression","Do Not Feel Safe in Relationship","Mania","Sleep Problems",
  ]},
  respiratory: { label:"Respiratory", items:[
    "Cough","Coughing Up Blood","Shortness of Breath","Sleep Apnea","Snoring","Wheezing",
  ]},
};

const initROS=()=>Object.fromEntries(
  Object.entries(ROS).map(([sys,{items}])=>[sys,Object.fromEntries(items.map(i=>[i,false]))])
);

export default function ReviewOfSystems({ patientName="", onSubmit }) {
  const history=useHistory();
  const formRef=useRef(null);
  const sigRef=useRef(null);
  const [sigEmpty,setSigEmpty]=useState(true);
  const [submitting,setSubmitting]=useState(false);
  const [savedId,setSavedId]=useState(null);
  const [showToast,Toast]=useToast();

  const [ros,setRos]=useState(initROS);
  const [weightGainLbs,setWeightGainLbs]=useState("");
  const [weightLossLbs,setWeightLossLbs]=useState("");
  const [eyeLastExam,setEyeLastExam]=useState("");
  const [additionalNotes,setNotes]=useState("");
  const [responsibleParty,setResponsibleParty]=useState(patientName);
  const [relationship,setRelationship]=useState("Self");
  const [sigDate,setSigDate]=useState(new Date().toISOString().split("T")[0]);

  const toggleRos=useCallback((sys,item)=>setRos(r=>({...r,[sys]:{...r[sys],[item]:!r[sys][item]}})),[]);
  const getSig=()=>(!sigRef.current||sigRef.current.isEmpty())?null:sigRef.current.toDataURL("image/png");

  const buildDoc=()=>({
    formType:"review_of_systems",
    patient:{name:patientName},
    reviewOfSystems:ros,
    weightGainLbs, weightLossLbs, eyeLastExam, additionalNotes,
    authorization:{responsibleParty,relationship,sigDate:sigDate||null,signatureDataURL:getSig()},
    meta:{submittedAt:new Date().toISOString(),formVersion:"1.0.0"},
  });

  const handleSubmit=async()=>{
    if(sigEmpty){showToast("Please sign before submitting.","error");return;}
    setSubmitting(true);
    try{
      const doc=buildDoc();
      const res=typeof onSubmit==="function"?await onSubmit(doc):await saveToMongo(doc,API_URL);
      if(res?._id)setSavedId(res._id);
      showToast("Saved! Redirecting…","success");
      setTimeout(()=>history.push(NEXT),1800);
    }catch(e){console.error(e);showToast("Save failed.","error");}
    finally{setSubmitting(false);}
  };
  const handleDownload=async()=>{
    try{await downloadPDF(formRef,`ros-${patientName||"patient"}.pdf`);showToast("Downloaded!");}
    catch{showToast("PDF failed.","error");}
  };

  return (
    <FormShell title="Review of Systems (ROS)"
      subtitle="Please check all that apply. If you are here for your heart, non-cardiac issues will be deferred to your primary care doctor."
      dbLabel="MongoDB · review_of_systems"
      patientName={patientName} savedId={savedId} formRef={formRef}
      onSubmit={handleSubmit} onDownload={handleDownload} submitting={submitting}>

      {Object.entries(ROS).map(([sys,{label,items}])=>(
        <div className="wf-section" key={sys}>
          <div className="wf-section-title">
            <span className="num" style={{fontSize:9,width:28,height:28}}>✓</span>{label}
          </div>
          <div className="wf-check-group">
            {items.map(item=>(
              <label className="wf-check-label" key={item}>
                <input type="checkbox" checked={!!ros[sys][item]} onChange={()=>toggleRos(sys,item)} />{item}
              </label>
            ))}
          </div>
          {sys==="constitutional"&&(
            <div className="wf-grid wf-grid-2" style={{marginTop:12}}>
              <Field label="Weight Gain (lbs)"><input type="number" value={weightGainLbs} onChange={e=>setWeightGainLbs(e.target.value)} /></Field>
              <Field label="Weight Loss (lbs)"><input type="number" value={weightLossLbs} onChange={e=>setWeightLossLbs(e.target.value)} /></Field>
            </div>
          )}
          {sys==="eyes"&&(
            <div style={{marginTop:12,maxWidth:240}}>
              <Field label="Date of Last Eye Exam"><input type="date" value={eyeLastExam} onChange={e=>setEyeLastExam(e.target.value)} /></Field>
            </div>
          )}
        </div>
      ))}

      <div className="wf-section">
        <div className="wf-section-title"><span className="num">+</span>Additional Information</div>
        <Field label="Any other health information your provider should know">
          <textarea value={additionalNotes} onChange={e=>setNotes(e.target.value)} placeholder="Add notes here…" style={{minHeight:80}} />
        </Field>
      </div>

      <SigBlock sigRef={sigRef} sigEmpty={sigEmpty} setSigEmpty={setSigEmpty}
        responsibleParty={responsibleParty} setResponsibleParty={setResponsibleParty}
        relationship={relationship} setRelationship={setRelationship}
        sigDate={sigDate} setSigDate={setSigDate} />

      {Toast}
    </FormShell>
  );
}
/*
── Mongoose Schema ────────────────────────────────────────────────────────────
const ROSSchema = new Schema({
  formType:{type:String,default:"review_of_systems"},
  patient:{name:String},
  reviewOfSystems:Schema.Types.Mixed,
  weightGainLbs:String, weightLossLbs:String, eyeLastExam:String, additionalNotes:String,
  authorization:{responsibleParty:String,relationship:String,sigDate:Date,signatureDataURL:String},
  meta:{submittedAt:Date,formVersion:String},
},{timestamps:true,collection:"review_of_systems"});
*/
